import { useCallback, useState } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Heading } from "@stubhub/broadway-web";
import { MultiVariateFeature, RecordParticipationTypes } from "@viagogo/feature";
import type { IBannerItem } from "src/http/api-types";
import { useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { GridCardBase } from "src/website-apps/shared/components/grid-card-base";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { CAROUSEL_SIZES, HOME_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";
import { usePrefersReducedMotion } from "src/website-apps/shared/components/responsive-carousel/usePrefersReducedMotion";
import { styles } from "./marquee-hero-carousel.recipes";

interface Props {
  items: readonly IBannerItem[];
}

interface MarqueeCarouselProps extends Props {
  autoAdvanceIntervalMs?: number | undefined;
  onOverflowChange: (hasOverflow: boolean) => void;
}

const headerContentId = "cid$#4242207#";

const AUTO_ADVANCE_FAST_MS = 5000;
const AUTO_ADVANCE_SLOW_MS = 10_000;

const MarqueeCarousel = ({ items, autoAdvanceIntervalMs, onOverflowChange }: MarqueeCarouselProps) => {
  const isDesktop = useMediaQuery(Breakpoint.Tablet);
  const header = useContent(headerContentId);
  return (
    <div>
      <Heading as="h2" variant="header">
        {header}
      </Heading>
      <div className={styles.container({ size: isDesktop ? "desktop" : "mobile" })}>
        <ResponsiveCarousel
          carouselId={HOME_CAROUSEL_IDS.heroBanner}
          classes={{ root: styles.scrollContainer({ size: isDesktop ? "desktop" : "mobile" }) }}
          hasHoverEffect={false}
          hidePaginationButtons={!isDesktop}
          items={items}
          gapPx={isDesktop ? 16 : 12}
          size={isDesktop ? CAROUSEL_SIZES.marqueeDesktop : CAROUSEL_SIZES.marqueeMobile}
          analyticsCarouselId={CarouselId.HeroBanner}
          analyticsItemType={CarouselItemType.Category}
          useFlex
          autoAdvanceIntervalMs={autoAdvanceIntervalMs}
          onOverflowChange={onOverflowChange}
          renderItem={(item) => (
            <GridCardBase
              headingAs="h2"
              url={item.url ?? ""}
              title={item.categoryName ?? ""}
              dateRangeStart={item.formattedLocalEventsDates?.split(" - ", 1)[0] ?? undefined}
              dateRangeEnd={item.formattedLocalEventsDates?.split(" - ", 2)[1] ?? undefined}
              imageUrl={item.imageUrl ?? ""}
              entityId={item.categoryId}
              entityType={ENTITY_TYPES.CATEGORY}
              variant="marquee"
              size={isDesktop ? "default" : "mobile"}
              fullHeightImage
              countdownDate={item.countdownDate ?? undefined}
            />
          )}
        />
      </div>
    </div>
  );
};

export const MarqueeHeroCarousel = ({ items }: Props) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [hasOverflow, setHasOverflow] = useState(false);
  const handleOverflowChange = useCallback((value: boolean) => setHasOverflow(value), []);
  // Only carousels that actually overflow can advance, so only they can show the treatment.
  const canParticipate = useCallback(() => !prefersReducedMotion && hasOverflow, [prefersReducedMotion, hasOverflow]);
  return (
    <MultiVariateFeature
      id={FeatureSetEnum.TMHomeMarqueeAutoAdvance}
      canParticipate={canParticipate}
      recordType={RecordParticipationTypes.IN_VIEW}
      control={() => <MarqueeCarousel items={items} onOverflowChange={handleOverflowChange} />}
      variants={[
        () => (
          <MarqueeCarousel
            items={items}
            autoAdvanceIntervalMs={AUTO_ADVANCE_FAST_MS}
            onOverflowChange={handleOverflowChange}
          />
        ),
        () => (
          <MarqueeCarousel
            items={items}
            autoAdvanceIntervalMs={AUTO_ADVANCE_SLOW_MS}
            onOverflowChange={handleOverflowChange}
          />
        ),
      ]}
    />
  );
};
